<template>
  <base-stack class="user-info">
    <base-stack as="dl" class="box remarks-box" v-if="user.remarks && user.remarks.length">
      <div v-for="remark in user.remarks" :key="remark.id" class="remark-item">
        <dt>{{ remark.createdAt | dayjs('MMM DD, YYYY') }}</dt>
        <dd>{{ remark.comment }}</dd>
      </div>
    </base-stack>
    <div class="user-info__content">
      <div class="data">
        <dl>
          <div>
            <dt>ID</dt>
            <dd>
              <a :href="googleAnalyticsRef" target="_blank">{{ user.id }}</a>
            </dd>
          </div>
          <div v-if="user.stripeId">
            <dt>Stripe ID</dt>
            <dd>
              <a :href="stripeUrl" target="_blank">{{ user.stripeId }}</a>
            </dd>
          </div>
          <div>
            <dt>Username</dt>
            <dd>{{ user.username }}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{{ user.email }}</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd>{{ user.role }}</dd>
          </div>
          <div v-if="user.score">
            <dt>Score</dt>
            <dd>{{ user.score && user.score.total }}</dd>
          </div>
          <div v-if="user.citizenshipExpiresAt">
            <dt>Citizenship expiration</dt>
            <dd>
              {{ user.citizenshipExpiresAt | dayjs('MMM DD, YYYY (HH:mm)') }}
            </dd>
          </div>
          <div v-if="user.citizenshipRemindedAt">
            <dt>Citizenship last reminded at</dt>
            <dd>
              {{ user.citizenshipRemindedAt | dayjs('MMM DD, YYYY (HH:mm)') }}
            </dd>
          </div>
          <div v-if="isCitizen">
            <dt>Consecutive citizen since</dt>
            <dd>{{ user.citizenSince | dayjs('MMM DD, YYYY (HH:mm)') }}</dd>
          </div>
          <div>
            <dt>Registered at</dt>
            <dd>{{ user.createdAt | dayjs('MMM DD, YYYY (HH:mm)') }}</dd>
          </div>
          <div>
            <dt>Last updated at</dt>
            <dd>{{ user.updatedAt | dayjs('MMM DD, YYYY (HH:mm)') }}</dd>
          </div>

          <dl class="box" v-if="user.address">
            <div>
              <h3>Address</h3>
            </div>
            <div>
              <dt>Name</dt>
              <dd>{{ user.name || user.address.name }}</dd>
            </div>
            <div>
              <dt>First address line</dt>
              <dd>{{ user.address.firstAddressLine }}</dd>
            </div>
            <div>
              <dt>Second address line</dt>
              <dd>{{ user.address.secondAddressLine }}</dd>
            </div>
            <div>
              <dt>Postal code</dt>
              <dd>{{ user.address.postalCode }}</dd>
            </div>
            <div>
              <dt>City</dt>
              <dd>{{ user.address.city }}</dd>
            </div>
            <div>
              <dt>Administrative area</dt>
              <dd>{{ user.address.administrativeArea }}</dd>
            </div>
            <div>
              <dt>Country</dt>
              <dd>{{ user.address.country.name }}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>{{ user.phone || user.address.phone }}</dd>
            </div>
          </dl>

          <dl class="box passports-box" v-if="user.passports && user.passports.length">
            <div>
              <h3>Passports</h3>
            </div>
            <div v-for="passport in user.passports" :key="passport.id" class="passport-item">
              <dt>
                <span class="passport-status" :class="{ expired: isExpired(passport) }">
                  {{ isExpired(passport) ? 'Expired' : 'Active' }}
                </span>
                <span v-if="passport.expiresAt" class="passport-date">
                  {{ passport.expiresAt | dayjs('MMM DD, YYYY') }}
                </span>
              </dt>
              <dd>
                <base-button v-if="isExpired(passport)" @click="unrevokePassport(passport.id)"> Unrevoke </base-button>
                <base-button v-else @click="revokePassport(passport.id)">Revoke</base-button>
              </dd>
            </div>
          </dl>
        </dl>
      </div>

      <div class="image-wrapper">
        <img v-if="user.imageUrl" :src="user.imageUrl" />
      </div>
    </div>
  </base-stack>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { IssuedPassport, User, UserRole } from '../types/api';

@Component
export default class UserInfo extends Vue {
  @Prop({ required: true }) user!: User;

  get isCitizen(): boolean {
    return this.user.role === UserRole.Citizen;
  }

  get stripeUrl(): string | null {
    if (!this.user.stripeId) {
      return null;
    }

    return `https://dashboard.stripe.com/customers/${this.user.stripeId}`;
  }

  get googleAnalyticsRef(): string {
    return `https://analytics.google.com/analytics/web/#/report/visitors-user-activity/a139929781w212433714p206082174/_r.userId=${this.user.id}&_r.userListReportStates=&_r.userListReportId=visitors-user-id/`;
  }

  isExpired(passport: IssuedPassport): boolean {
    if (!passport.expiresAt) return false;
    return new Date(passport.expiresAt) < new Date();
  }

  async revokePassport(id: string): Promise<void> {
    const confirm = await this.$modal.confirm({
      title: 'Revoke passport',
      message: 'Are you sure you want to revoke this passport?',
    });
    if (!confirm) return;
    await this.$apollo.mutate({
      mutation: gql`
        mutation AdminRevokePassport($id: ID!) {
          adminRevokePassport(id: $id) {
            passport {
              id
              expiresAt
            }
          }
        }
      `,
      variables: { id },
      refetchQueries: ['User'],
      awaitRefetchQueries: true,
    });
  }

  async unrevokePassport(id: string): Promise<void> {
    const confirm = await this.$modal.confirm({
      title: 'Unrevoke passport',
      message: 'Are you sure you want to restore this passport?',
    });
    if (!confirm) return;
    await this.$apollo.mutate({
      mutation: gql`
        mutation AdminUnrevokePassport($id: ID!) {
          adminUnrevokePassport(id: $id) {
            passport {
              id
              expiresAt
            }
          }
        }
      `,
      variables: { id },
      refetchQueries: ['User'],
      awaitRefetchQueries: true,
    });
  }
}
</script>

<style lang="scss" scoped>
.user-info {
  padding: 1rem 0;

  &__content {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap-reverse;
    justify-content: space-between;

    .data {
      max-width: 800px;
      margin-right: 24px;
      flex: 1 1 500px;
    }

    dl {
      margin-top: 16px;

      > div {
        display: flex;
        justify-content: space-between;
        dt {
          margin-right: 24px;
        }
        dd {
          text-align: right;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }

    .image-wrapper {
      max-width: 200px;
      flex: 0 0 200px;
      margin-bottom: 24px;
      img {
        object-fit: contain;
        width: 100%;
        height: 100%;
      }
    }
  }

  .box {
    padding: 16px;
    border: 1px solid $color-theme-red;

    h3 {
      font-weight: bold;
    }
  }

  .remarks-box {
    background-color: rgba($color-theme-red, 0.1);

    .remark-item {
      display: flex;
      gap: 16px;
      justify-content: space-between;

      dt {
        flex-shrink: 0;
        opacity: 0.7;
      }

      dd {
        text-align: right;
      }
    }
  }

  .passports-box {
    .passport-item {
      display: flex;
      gap: 16px;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;

      dt {
        display: flex;
        gap: 8px;
        align-items: center;
      }

      .passport-status {
        font-weight: bold;
        color: green;

        &.expired {
          color: $color-theme-red;
        }
      }

      .passport-date {
        opacity: 0.7;
        font-size: 0.9em;
      }
    }
  }
}
</style>
